import { AlertTriangle, CheckCircle, Clock, Info, XCircle } from 'lucide-react';
import React from 'react';
import Layout from './Layout';

const VARIANTS = {
  success:  { bg: 'bg-green-50',  border: 'border-green-200', icon: CheckCircle,   iconColor: 'text-green-600' },
  warning:  { bg: 'bg-yellow-50', border: 'border-yellow-200', icon: Clock,         iconColor: 'text-yellow-600' },
  orange:   { bg: 'bg-orange-50', border: 'border-orange-200', icon: Info,           iconColor: 'text-orange-500' },
  error:    { bg: 'bg-red-50',    border: 'border-red-200',    icon: AlertTriangle, iconColor: 'text-red-600' },
  blocked:  { bg: 'bg-red-50',    border: 'border-red-200',    icon: XCircle,       iconColor: 'text-red-600' },
  neutral:  { bg: 'bg-gray-50',   border: 'border-gray-200',   icon: Info,          iconColor: 'text-gray-500' },
};

/**
 * Unified status/error page component.
 *
 * @param {string}  variant     - success | warning | orange | error | blocked | neutral
 * @param {string}  title       - Status bar title
 * @param {string}  description - Status bar description
 * @param {React.ReactNode} children - Optional body content (info cards, etc.)
 * @param {Array}   actions     - Array of { label, onClick, primary?, icon? }
 * @param {object}  step        - { current, total } for progress dots
 * @param {string}  detail      - Optional error detail (shown in code box)
 */
const StatusPage = ({
  variant = 'neutral',
  title,
  description,
  children,
  actions = [],
  step,
  detail,
  onBack,
  backLabel = 'Back',
}) => {
  const v = VARIANTS[variant] || VARIANTS.neutral;
  const IconComponent = v.icon;

  return (
    <Layout
      step={step?.current}
      totalSteps={step?.total}
      onBack={onBack}
      backLabel={backLabel}
    >
      <div className="space-y-4">
        {/* Status bar */}
        <div className={`rounded border p-4 ${v.bg} ${v.border}`}>
          <div className="flex items-center gap-2 mb-1">
            <IconComponent className={`w-5 h-5 ${v.iconColor} flex-shrink-0`} />
            <h2 className="text-base font-bold text-gray-900">{title}</h2>
          </div>
          {description && (
            <p className="text-sm text-gray-600 ml-7">{description}</p>
          )}
        </div>

        {/* Optional body */}
        {children}

        {/* Optional error detail */}
        {detail && (
            <div className="rounded border border-gray-200 bg-gray-50 px-4 py-3">
            <p className="text-xs text-gray-500 mb-1">Error detail</p>
            <p className="text-sm text-gray-700 font-mono break-all">{detail}</p>
          </div>
        )}

        {/* Actions */}
        {actions.length > 0 && (
          <div className={`flex gap-3 ${actions.length === 1 ? '' : ''}`}>
            {actions.map((action, i) => (
              <button
                key={i}
                onClick={action.onClick}
                disabled={action.disabled}
                className={`flex-1 flex items-center justify-center gap-2 py-3 rounded font-semibold text-sm transition-colors ${
                  action.primary
                    ? 'bg-primary-600 text-white hover:bg-primary-700'
                    : 'border border-gray-300 text-gray-700 hover:bg-gray-50'
                } ${action.disabled ? 'opacity-50 cursor-not-allowed' : ''}`}
              >
                {action.icon && <action.icon className="w-4 h-4" />}
                {action.label}
              </button>
            ))}
          </div>
        )}

        {/* Footer */}
        <p className="text-center text-xs text-gray-400 pt-1">For support, contact support@koinremit.com</p>
      </div>
    </Layout>
  );
};

/**
 * Info card with key-value rows.
 * @param {Array} rows - Array of { label, value, valueClass? }
 */
export const InfoCard = ({ rows = [] }) => (
  <div className="overflow-hidden rounded border border-gray-200 bg-white">
    <div className="divide-y divide-gray-100">
      {rows.map((row, i) => (
        <div key={i} className="flex justify-between items-center px-5 py-3">
          <span className="text-sm text-gray-500">{row.label}</span>
          <span className={`text-sm font-semibold ${row.valueClass || 'text-gray-900'}`}>
            {row.value}
          </span>
        </div>
      ))}
    </div>
  </div>
);

export default StatusPage;
